/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Dispatch, Action, type ActionCreator } from '@reduxjs/toolkit';
import axios from 'axios';
import get from 'lodash/get';
import { SecurityHelper } from 'sarsaparilla';
import {
    CAMPSITE_CAMPING_TYPE,
    CAMPSITE_DAYUSE_TYPE,
} from 'ui-search/src/constants/filters/camping';
import { RECOMMENDATION_FAILED, RECOMMENDATION_RESULTS } from '../../../shared/constants';
import {
    isValidSimpleDate,
    searchDateToUTCString,
} from '../../../shared/utils/dateUtils';

export const DATE_FORMAT_US_SLASHES = 'MM/DD/YYYY';

export interface AddReferrerParams {
    model?: string;
    pageReferrer?: string;
    entityId?: string;
    entityType?: string;
    locationId?: string;
    nthItem?: number;
}

export interface GetRecommendationsParams {
    model?: string;
    location?: string;
    area?: any;
    state?: any;
    startDate?: string;
    endDate?: string;
    includeUnavailable?: boolean;
    includeNotReservable?: boolean;
    radius?: any;
    fq?: string[];
    fg?: string[];
    size?: number;
}

const INVENTORY_MAPPING: Record<string, string> = {
    [CAMPSITE_DAYUSE_TYPE]: 'dayuse',
    [CAMPSITE_CAMPING_TYPE]: 'camping',
};

export function addReferrer({
    model,
    pageReferrer,
    entityId,
    entityType,
    locationId,
    nthItem,
}: AddReferrerParams) {
    const storedRefs = window.localStorage.getItem('recommendation-refs');
    const refs: Record<string, any> = storedRefs ? JSON.parse(storedRefs) : {};

    const referrer = `${model}:${pageReferrer}:${locationId}:${nthItem}`;

    if (entityId) {
        refs[entityId] = {
            referrer,
            model,
            pageReferrer,
            entityId,
            entityType,
            locationId,
            nthItem,
        };
    }

    window.localStorage.setItem('recommendation-refs', JSON.stringify(refs));
}

export function getReferrerForLocation(locationId: string) {
    const refs = window.localStorage.getItem('recommendation-refs');
    if (refs) {
        return JSON.parse(refs)[locationId];
    }
    return null;
}

export function getRecommendationReferrerForLocation(locationId: string) {
    const referrer = getReferrerForLocation(locationId);
    if (referrer != null) {
        return referrer.referrer;
    }
    return null;
}

export function checkLogAddToCartConversion(locationId: string) {
    return (_dispatch: Dispatch, getState: () => any) => {
        const referrer = getReferrerForLocation(locationId);
        const ldClient = getState().launchdarkly.ldClient;
        if (referrer != null && ldClient != null) {
            ldClient.track('recommendation-models-add-to-cart', {
                location_id: locationId,
                model: referrer.model,
            });
        }
    };
}

const recommendationResults: ActionCreator<Action> = (
    results: Record<string, any>[]
) => ({
    type: RECOMMENDATION_RESULTS,
    results,
});

const recommendationFailed: ActionCreator<Action> = (errorMsg: string) => ({
    type: RECOMMENDATION_FAILED,
    errorMsg,
});

export function getRecommendations({
    model,
    location,
    area,
    state,
    startDate,
    endDate,
    includeUnavailable,
    includeNotReservable,
    radius,
    fq,
    size = 50,
    fg,
}: GetRecommendationsParams) {
    return async (dispatch: Dispatch) => {
        try {
            const m = model || 'similar1';
            let url = `${process.env['API']}/recommendation/recommend?m=${m}`;
            if (location) {
                url += `&l=${location}`;
            }
            if (area) {
                url += `&area=${area}`;
            }
            if (state) {
                url += `&state=${state}`;
            }
            if (startDate) {
                let dt = startDate;
                if (dt.includes('/') && isValidSimpleDate(dt)) {
                    dt = searchDateToUTCString(dt);
                }
                url += `&start_date=${dt}`;
            }
            if (endDate) {
                let dt = endDate;
                if (dt.includes('/') && isValidSimpleDate(dt)) {
                    dt = searchDateToUTCString(dt);
                }
                url += `&end_date=${dt}`;
            }
            if (includeUnavailable) {
                url += '&include_unavailable=true';
            }
            if (includeNotReservable) {
                url += '&include_notreservable=true';
            }
            if (radius) {
                url += `&radius=${radius}`;
            }
            if (fq && fq?.length > 0) {
                const queryParams = new URLSearchParams('');
                fq.forEach((item) => queryParams.append('fq', item));
                url += `&${queryParams.toString()}`;
            }
            let fgParams = [...(fg ?? [])];
            // If both site use types are selected, we leave them out of the query.
            // That way, the recommendation service knows to include all Site Use types.
            const omitSet = new Set([CAMPSITE_DAYUSE_TYPE, CAMPSITE_CAMPING_TYPE]);
            const hasAllSiteUseTypes = [...omitSet].every((type) =>
                fgParams.includes(type)
            );

            if (hasAllSiteUseTypes) {
                fgParams = fgParams.filter((item) => !omitSet.has(item));
            }

            // Map site use types to their corresponding inventory types
            fgParams = fgParams.map((item) => INVENTORY_MAPPING[item] || item);

            if (fgParams.length > 0) {
                const queryParams = new URLSearchParams('');
                fgParams.forEach((item) => queryParams.append('fg', item));
                url += `&${queryParams.toString()}`;
            }
            url += `&size=${size}`;

            const response = await axios.get(url, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            });
            const results = get(response, 'data.results');
            if (results) {
                dispatch(recommendationResults(results));
            }
        } catch {
            dispatch(recommendationFailed('Server Error'));
        }
    };
}
